import Cookies from 'js-cookie'
import { useCallback, useEffect, useState } from 'react'

type TableState = { sortBy: string[]; sortDesc: boolean[] }
type TableStates = Record<string, TableState>

export interface UseOpportunityTableStateOptions {
  /** 現在選択されているタブ */
  selectedTab: string
}

/**
 * テーブルのソートとページサイズの状態を管理するカスタムフック
 *
 * タブごとに異なる状態を保持し、Cookieに永続化することで
 * ページ遷移後も設定を復元します。
 */
export function useOpportunityTableState(options: UseOpportunityTableStateOptions) {
  const { selectedTab } = options

  // ページサイズの状態（タブごとに保存）
  const [pageSize, setPageSize] = useState<number>(() => {
    const savedPageSizes: Record<string, number> | null = JSON.parse(Cookies.get('opportunities-page-size') || 'null')
    return savedPageSizes?.[selectedTab] || 100
  })

  // テーブルソート状態（タブごとに保存）
  const [sortKey, setSortKey] = useState<string | null>(() => {
    const savedStates: TableStates | null = JSON.parse(Cookies.get('opportunities-table-state') || 'null')
    const tabState = savedStates?.[selectedTab]
    return tabState ? tabState.sortBy?.[0] || null : 'updated_at'
  })

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const savedStates: TableStates | null = JSON.parse(Cookies.get('opportunities-table-state') || 'null')
    const tabState = savedStates?.[selectedTab]
    return tabState ? (tabState.sortDesc?.[0] ? 'desc' : 'asc') : 'desc'
  })

  // タブ変更時にソート状態とページサイズを復元
  useEffect(() => {
    const savedStates: TableStates | null = JSON.parse(Cookies.get('opportunities-table-state') || 'null')
    const tabState = savedStates?.[selectedTab]
    if (tabState) {
      setSortKey(tabState.sortBy?.[0] || null)
      setSortDirection(tabState.sortDesc?.[0] ? 'desc' : 'asc')
    } else {
      // デフォルトは更新日時降順
      setSortKey('updated_at')
      setSortDirection('desc')
    }

    // ページサイズを復元
    const savedPageSizes: Record<string, number> | null = JSON.parse(Cookies.get('opportunities-page-size') || 'null')
    setPageSize(savedPageSizes?.[selectedTab] || 100)
  }, [selectedTab])

  // ソート変更時にCookieに保存
  const handleSortChange = useCallback(
    (key: string | null, direction: 'asc' | 'desc') => {
      setSortKey(key)
      setSortDirection(direction)

      // Cookieに保存
      const savedStates: TableStates = JSON.parse(Cookies.get('opportunities-table-state') || '{}')
      const newState: TableState = {
        sortBy: key ? [key] : [],
        sortDesc: key ? [direction === 'desc'] : [],
      }

      // 現在のタブの状態と異なる場合のみ保存
      const existingState = savedStates[selectedTab]
      const isDifferent =
        !existingState ||
        existingState.sortBy?.[0] !== newState.sortBy[0] ||
        existingState.sortDesc?.[0] !== newState.sortDesc[0]

      if (isDifferent) {
        savedStates[selectedTab] = newState
        Cookies.set('opportunities-table-state', JSON.stringify(savedStates), {
          secure: window.location.protocol === 'https:',
          expires: 30,
          path: '/',
        })
      }
    },
    [selectedTab],
  )

  // ページサイズ変更時にCookieに保存
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize)

      // Cookieに保存
      const savedPageSizes: Record<string, number> = JSON.parse(Cookies.get('opportunities-page-size') || '{}')
      savedPageSizes[selectedTab] = newPageSize
      Cookies.set('opportunities-page-size', JSON.stringify(savedPageSizes), {
        secure: window.location.protocol === 'https:',
        expires: 30,
        path: '/',
      })
    },
    [selectedTab],
  )

  return {
    pageSize,
    sortKey,
    sortDirection,
    handleSortChange,
    handlePageSizeChange,
  }
}
